/* Progressive campaign enhancement; no cart requests, price changes or media rewrites. */
(() => {
  if (!customElements.get('wi-cro-carousel')) {
    class WorthItCampaignCarousel extends HTMLElement {
      connectedCallback() {
        if (this._controller) return;
        this._slides = Array.from(this.querySelectorAll('[data-wi-cro-slide]'));
        if (this._slides.length < 2) return;
        this._controller = new AbortController();
        const signal = this._controller.signal;
        this._status = this.querySelector('[data-wi-cro-status]');
        this._rotation = this.querySelector('[data-wi-cro-rotation]');
        this._dots = Array.from(this.querySelectorAll('[data-wi-cro-dot]'));
        this._dots.forEach((dot) => dot.addEventListener('click', () => {
          this._userPaused = true;
          this._show(Number(dot.dataset.wiCroDot), true);
          this._syncRotation();
        }, { signal }));
        this._motion = window.matchMedia('(prefers-reduced-motion: reduce)');
        this._index = Math.max(0, this._slides.findIndex((slide) => !slide.hidden));
        this._userPaused = this._userPaused ?? this._motion.matches;
        this._hoverPaused = false;
        this._inView = typeof IntersectionObserver === 'undefined';
        this.querySelector('[data-wi-cro-controls]').hidden = false;
        this._slides.forEach((slide, index) => {
          slide.setAttribute('aria-label', `${index + 1} of ${this._slides.length}: ${slide.getAttribute('aria-label').replace(/^\d+ of \d+: /, '')}`);
        });
        this.querySelector('[data-wi-cro-prev]').addEventListener('click', () => this._move(-1), { signal });
        this.querySelector('[data-wi-cro-next]').addEventListener('click', () => this._move(1), { signal });
        this._rotation.addEventListener('pointerdown', () => {
          // Preserve the visible button action before pointer focus pauses it.
          this._pointerRotationAction = !this._userPaused;
        }, { signal });
        this._rotation.addEventListener('keydown', () => { this._pointerRotationAction = undefined; }, { signal });
        this._rotation.addEventListener('pointercancel', () => { this._pointerRotationAction = undefined; }, { signal });
        this._rotation.addEventListener('blur', () => { this._pointerRotationAction = undefined; }, { signal });
        this._rotation.addEventListener('click', () => {
          this._userPaused = this._pointerRotationAction ?? !this._userPaused;
          this._pointerRotationAction = undefined;
          this._syncRotation();
        }, { signal });
        this.addEventListener('focusin', () => {
          // Focus pauses rotation persistently; only explicit Play resumes it.
          this._userPaused = true;
          this._syncRotation();
        }, { signal });
        this.addEventListener('mouseenter', () => { this._hoverPaused = true; this._schedule(); }, { signal });
        this.addEventListener('mouseleave', () => { this._hoverPaused = false; this._schedule(); }, { signal });
        this.addEventListener('keydown', (event) => {
          if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
          if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
          if (event.target.closest('input,select,textarea')) return;
          event.preventDefault();
          this._move(event.key === 'ArrowLeft' ? -1 : 1);
        }, { signal });
        document.addEventListener('visibilitychange', () => this._schedule(), { signal });
        this._motion.addEventListener('change', () => {
          if (this._motion.matches) this._userPaused = true;
          this._syncRotation();
        }, { signal });
        if (typeof IntersectionObserver !== 'undefined') {
          this._observer = new IntersectionObserver((entries) => {
            this._inView = entries.some((entry) => entry.isIntersecting && entry.intersectionRatio >= 0.5);
            this._schedule();
          }, { threshold: [0, 0.5] });
          this._observer.observe(this);
        }
        this._show(this._index, false);
        this._syncRotation();
      }
      _move(direction) {
        this._userPaused = true;
        this._show((this._index + direction + this._slides.length) % this._slides.length, true);
        this._syncRotation();
      }
      _show(index, announce) {
        const focusedSlide = this._slides.find((slide) => slide.contains(document.activeElement));
        const restoreSlideFocus = focusedSlide && focusedSlide !== this._slides[index];
        this._index = index;
        this._dots.forEach((dot, position) => {
          if (position === index) dot.setAttribute('aria-current', 'true');
          else dot.removeAttribute('aria-current');
        });
        this._slides.forEach((slide, position) => { slide.hidden = position !== index; });
        this._status.setAttribute('aria-live', announce ? 'polite' : 'off');
        this._status.textContent = `${index + 1} / ${this._slides.length}`;
        if (restoreSlideFocus) this._slides[index].querySelector('a,button')?.focus({ preventScroll: true });
      }
      _syncRotation() {
        this._rotation.disabled = this._motion.matches;
        this._rotation.textContent = this._motion.matches ? 'Motion paused' : this._userPaused ? 'Play motion' : 'Pause motion';
        this._schedule();
      }
      _schedule() {
        clearTimeout(this._timer);
        this._timer = null;
        const paused = !this.isConnected || !this._controller || this._motion.matches || this._userPaused || this._hoverPaused || !this._inView || document.hidden;
        this.dataset.wiCroMotion = paused ? 'paused' : 'active';
        if (paused) return;
        this._timer = setTimeout(() => {
          if (!this._controller) return;
          this._show((this._index + 1) % this._slides.length, false);
          this._schedule();
        }, 6000);
      }
      disconnectedCallback() {
        this.dataset.wiCroMotion = 'paused';
        clearTimeout(this._timer);
        this._timer = null;
        this._controller?.abort();
        this._controller = null;
        this._observer?.disconnect();
        this._observer = null;
      }
    }
    customElements.define('wi-cro-carousel', WorthItCampaignCarousel);
  }

  // Root emits only after an accepted Add, current confirmed cart and successful
  // cart paint. Generic cart-update is deliberately never used for celebration.
  if (window.WorthItCROCelebrationBound) return;
  window.WorthItCROCelebrationBound = true;
  let celebrated = false;
  const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
  document.addEventListener('worthit:cart-added', (event) => {
    if (!event.detail?.variantId || celebrated || motion.matches || document.hidden || !document.body || !Element.prototype.animate) return;
    try { if (sessionStorage.getItem('worthit-cart-celebrated') === '1') return; } catch { /* The in-memory guard still prevents repeat bursts. */ }
    celebrated = true;
    try { sessionStorage.setItem('worthit-cart-celebrated', '1'); } catch { /* Storage is optional. */ }
    const layer = document.createElement('div');
    layer.className = 'wi-cro-confetti';
    layer.setAttribute('aria-hidden', 'true');
    const animations = [];
    const finish = () => {
      animations.forEach((animation) => animation.cancel());
      layer.remove();
      document.removeEventListener('visibilitychange', hidden);
      motion.removeEventListener('change', reduced);
    };
    const hidden = () => { if (document.hidden) finish(); };
    const reduced = () => { if (motion.matches) finish(); };
    document.addEventListener('visibilitychange', hidden);
    motion.addEventListener('change', reduced);
    for (let index = 0; index < 14; index += 1) {
      const piece = document.createElement('span');
      piece.className = 'wi-cro-confetti__piece';
      piece.style.background = index % 3 === 0 ? '#5923a0' : index % 3 === 1 ? '#d4ff35' : '#b992ed';
      layer.appendChild(piece);
      const direction = index / 13 * Math.PI;
      const x = Math.cos(direction) * 130;
      const y = Math.sin(direction) * -90;
      animations.push(piece.animate([
        { transform: 'translate(0,0) rotate(0deg)', opacity: 0 },
        { transform: `translate(${x}px,${y}px) rotate(${index * 31}deg)`, opacity: 1, offset: 0.32 },
        { transform: `translate(${x * 1.2}px,${150 + index * 3}px) rotate(${index * 51}deg)`, opacity: 0 },
      ], { duration: 900 + index * 12, easing: 'ease-out', fill: 'forwards' }));
    }
    document.body.appendChild(layer);
    setTimeout(finish, 1200);
  });
})();
